"use client";

import React from "react";
import Image from "next/image";
import Link from "next/link";
import ScrollSpyLayout, {
  formatScrollSpyNavIndex,
  type ScrollSpyItem,
} from "@/components/ui/scroll-spy-layout";
import { useQueryState, parseAsString } from "nuqs";
import AutoScroll from "embla-carousel-auto-scroll";

import Page from "@/components/ui/page";
import { useTina } from "@/utils";
import Header from "@/components/Header";
import CallToAction from "@/components/CallToAction";
import Video from "@/components/Video";
import Section from "@/components/ui/section";
import TestimonialsCarousel from "@/components/TestimonialsCarousel";
import LogosCarousel from "@/components/LogosCarousel";
import { TeamMemberCard, TeamMemberDialog } from "@/components/TeamMember";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { TrophyIcon } from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "@/components/ui/carousel";
import { Button } from "@/components/ui/button";
import { cn, tinaFieldSafe } from "@/utils";
import { WheelGesturesPlugin } from "embla-carousel-wheel-gestures";
import { useGlobalData } from "@/components/TinaContext";
import { flattenAboutTeamMembers, getMemberSlug } from "@/lib/team-members";

export default function About(props: any) {
  const postCounts = props.postCounts;
  const { data } = useTina({
    query: props.query,
    variables: props.variables,
    data: props.data,
  });

  const [selectedMember, setSelectedMember] = useQueryState(
    "member",
    parseAsString.withDefault("")
  );

  const header = data?.about?.header;
  const video = data?.about?.video?.videoResource;
  const missionStatement = data?.about?.missionStatement;
  const teamIntro = data?.about?.teamIntro;
  const quote = data?.about?.quote;
  const heads = data?.about?.heads;
  const departments = data?.about?.departments;
  const milestones = data?.about?.milestones;
  const investors = data?.about?.investors;
  const awards = data?.about?.awards;
  const callToAction = data?.about?.cta;

  const allMembers = React.useMemo(
    () => flattenAboutTeamMembers({ heads, departments }),
    [heads, departments]
  );

  const currentMember = allMembers.find(
    (m) => getMemberSlug(m) === selectedMember
  );

  const handleMemberClick = (member: any) => {
    setSelectedMember(getMemberSlug(member));
  };

  const handleCloseDialog = () => {
    setSelectedMember("");
  };

  return (
    <Page
      defaultTinaField={data?.about}
      seo={data?.about?.seo}
      pagePath="/about"
      titleFallback="About"
    >
      <Header {...header} variant="left" backgroundVariant="pixels-l-r">
        {video && (
          <Video
            video={video}
            variant="card"
            size="lg"
            animateOnHover={false}
          />
        )}
      </Header>
      <MissionStatementSection missionStatement={missionStatement} />
      <TeamIntroSection intro={teamIntro} />
      <QuoteSection quote={quote} />
      <DepartmentsSection
        departments={departments}
        onMemberClick={handleMemberClick}
        postCounts={postCounts}
      />
      <HeadsSection
        heads={heads}
        onMemberClick={handleMemberClick}
        postCounts={postCounts}
      />
      <Milestones milestones={milestones} />
      <Investors investors={investors} />
      <Awards awards={awards} />
      <CallToAction
        {...callToAction}
        variant="left"
        backgroundVariant="dots"
      />

      <TeamMemberDialog
        member={currentMember}
        open={!!selectedMember}
        onClose={handleCloseDialog}
        postCount={postCounts[getMemberSlug(currentMember)] ?? 0}
      />
    </Page>
  );
}

function MissionStatementSection({
  missionStatement,
}: {
  missionStatement: any;
}) {
  if (!missionStatement?.title && !missionStatement?.subtitle) return null;
  return (
    <Section wrapperClassName="overflow-hidden !pb-8" className="space-y-4">
      {missionStatement.title && (
        <h2
          className="h2"
          data-tina-field={tinaFieldSafe(missionStatement, "title")}
        >
          {missionStatement.title}
        </h2>
      )}
      {missionStatement.subtitle && (
        <p
          className="whitespace-pre-wrap text-lg text-muted-foreground"
          data-tina-field={tinaFieldSafe(missionStatement, "subtitle")}
        >
          {missionStatement.subtitle}
        </p>
      )}
    </Section>
  );
}

function TeamIntroSection({ intro }: { intro: any }) {
  const autoScrollPlugin = React.useRef(
    AutoScroll({
      speed: 1,
      stopOnInteraction: false,
      stopOnMouseEnter: false,
    })
  );

  if (!intro) return null;

  return (
    <>
      <Section wrapperClassName="overflow-hidden" className="space-y-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-32">
          <div className="space-y-4">
            <h2 className="h2" data-tina-field={tinaFieldSafe(intro, "title")}>
              {intro?.title}
            </h2>
            {intro.subtitle && (
              <p
                className="h5"
                data-tina-field={tinaFieldSafe(intro, "subtitle")}
              >
                {intro?.subtitle}
              </p>
            )}
          </div>
          <p
            className="whitespace-pre-wrap"
            data-tina-field={tinaFieldSafe(intro, "description")}
          >
            {intro?.description}
          </p>
        </div>
      </Section>
      <div className="w-screen overflow-hidden !pb-12 lg:!pb-24">
        {intro.images && intro.images.length > 0 && (
          <Carousel
            className="w-full [&>div]:overflow-visible"
            opts={{ align: "center", loop: true }}
            plugins={[autoScrollPlugin.current as any]}
          >
            <CarouselContent className="">
              {intro.images.map((item: any, index: number) => (
                <CarouselItem key={index} className="pl-6 basis-auto">
                  <div
                    className="relative h-[240px] md:h-[320px] lg:h-[400px] overflow-hidden"
                    data-tina-field={tinaFieldSafe(item, "image")}
                  >
                    <Image
                      src={item.image}
                      alt={item.alt || `Team image ${index + 1}`}
                      height={400}
                      width={600}
                      style={{ width: "auto", height: "400px" }}
                      quality={100}
                      className="object-cover"
                    />
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
        )}
      </div>
    </>
  );
}

function QuoteSection({ quote }: { quote: any }) {
  if (!quote) return null;
  return (
    <Section
      backgroundVariant="pixels-subtle"
      className="[&_[data-slot='carousel-item']]:basis-1/1 [&_[data-slot='carousel-item']_p]:lg:!text-2xl [&_[data-slot='card-content']]:lg:grid-cols-[392px_1fr] [&_[data-slot='card-content']_div]:justify-center [&_[data-slot='card-content']_div]:gap-8"
    >
      <TestimonialsCarousel testimonials={[quote]} showGlobalButton={false} />
    </Section>
  );
}

function HeadsSection({
  heads,
  onMemberClick,
  postCounts,
}: {
  heads: any;
  onMemberClick: (member: any) => void;
  postCounts: Record<string, number>;
}) {
  if (!heads || !heads.teamMember || heads.teamMember.length === 0) return null;
  return (
    <Section wrapperClassName="!pt-16 lg:!pt-32 !pb-16 lg:!pb-32" className="space-y-8" id="founders">
      <div className="space-y-4">
        <h2 className="h2" data-tina-field={tinaFieldSafe(heads, "title")}>
          {heads?.title}
        </h2>
        <p
          className="text-lg text-muted-foreground"
          data-tina-field={tinaFieldSafe(heads, "subtitle")}
        >
          {heads?.subtitle}
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {heads.teamMember.map((member: any, index: number) => (
          <TeamMemberCard
            key={index}
            member={member}
            onClick={() => onMemberClick(member)}
            postCount={postCounts[getMemberSlug(member)] ?? 0}
          />
        ))}
      </div>
    </Section>
  );
}

function DepartmentsSection({
  departments,
  onMemberClick,
  postCounts,
}: {
  departments: any;
  onMemberClick: (member: any) => void;
  postCounts: Record<string, number>;
}) {
  if (
    !departments ||
    !departments.departments ||
    departments.departments.length === 0
  )
    return null;

  const items: ScrollSpyItem<any>[] = departments.departments.map(
    (dept: any) => ({
      id: dept?.title?.toLowerCase().replace(/ /g, "-") || "",
      label: dept?.title || "Department",
      data: dept,
    })
  );

  return (
    <Section
      // wrapperClassName="!pt-16 lg:!pt-32 !pb-16 lg:!pb-32"
      wrapperClassName="!pb-0"
      className="relative"
    >
      <ScrollSpyLayout<any>
        items={items}
        classNames={{
          root: "grid grid-cols-1 lg:grid-cols-[250px_1fr] relative gap-12 md:gap-20",
          nav: "lg:sticky top-20 z-10 self-start mt-2",
          content: "space-y-20 min-w-0",
          section: "w-full overflow-hidden min-w-0 space-y-8",
        }}
        renderNavItem={(item, index) => (
          <>
            <span className="font-mono">{formatScrollSpyNavIndex(index)}</span>
            {item.label}
          </>
        )}
        renderSection={(item, index) => {
          const department = item.data;
          return (
            <>
              <div className="space-y-2" id={index === 0 ? "team" : ""}>
                <h2
                  className="h3 capitalize"
                  data-tina-field={tinaFieldSafe(department, "title")}
                >
                  {department.title}
                </h2>
                {department.subtitle && (
                  <p
                    className="text-lg text-muted-foreground"
                    data-tina-field={tinaFieldSafe(department, "subtitle")}
                  >
                    {department.subtitle}
                  </p>
                )}
              </div>
              {department.teamMember && department.teamMember.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6">
                  {department.teamMember.map(
                    (member: any, memberIndex: number) => (
                      <TeamMemberCard
                        key={memberIndex}
                        member={member}
                        onClick={() => onMemberClick(member)}
                        postCount={postCounts[getMemberSlug(member)] ?? 0}
                      />
                    )
                  )}
                </div>
              )}
            </>
          );
        }}
      />
    </Section>
  );
}

function Milestones({ milestones }: { milestones: any }) {
  const [api, setApi] = React.useState<CarouselApi>();
  const [selectedIndex, setSelectedIndex] = React.useState(0);

  React.useEffect(() => {
    if (!api) return;

    const onSelect = () => {
      setSelectedIndex(api.selectedScrollSnap());
    };

    api.on("select", onSelect);
    onSelect();

    return () => {
      api.off("select", onSelect);
    };
  }, [api]);

  if (!milestones?.milestones || milestones.milestones.length === 0)
    return null;

  const milestonesData = milestones.milestones;

  return (
    <Section
      wrapperClassName="bg-background-light overflow-hidden"
      className="space-y-12"
    >
      {/* <h2 className="h2">{milestones.title}</h2> */}

      <div className="space-y-12">
        {/* Carousel */}
        <Carousel
          setApi={setApi}
          className="cursor-grab focus:cursor-grabbing active:cursor-grabbing [&>div]:!overflow-visible space-y-12"
          plugins={[WheelGesturesPlugin()]}
          opts={{
            skipSnaps: true,
            duration: 20,
            align: "start",
            loop: false,
          }}
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <h2
              className="h2"
              data-tina-field={tinaFieldSafe(milestones, "title")}
            >
              {milestones.title}
            </h2>
            {milestonesData.length > 1 && (
              <div className="relative flex flex-row-reverse md:flex-row gap-4 md:gap-6 items-center justify-between">
                <div className="font-mono text-sm">
                  <span className="text-secondary-400 mr-2">
                    {String(selectedIndex + 1).padStart(2, "0")}
                  </span>
                  / {String(milestonesData.length).padStart(2, "0")}
                </div>
                <div className="flex gap-4">
                  <CarouselPrevious className="relative !top-0 !left-0 !translate-x-0 !translate-y-0" />
                  <CarouselNext className="relative !top-0 !right-0 !translate-x-0 !translate-y-0" />
                </div>
              </div>
            )}
          </div>
          <CarouselContent className="cursor-grab focus:cursor-grabbing active:cursor-grabbing ml-4">
            {milestonesData.map((milestone: any, index: number) => (
              <CarouselItem
                key={index}
                className="pl-0 md:basis-1/2 lg:basis-1/3 border-t border-border"
              >
                <div className="space-y-8 border-l border-border !pt-10 pr-10 relative">
                  <div
                    className={cn(
                      "w-4 h-4 bg-white rounded-full absolute -left-0 top-0 -translate-x-1/2 -translate-y-1/2",
                      index === selectedIndex ? "bg-secondary-600" : "bg-white"
                    )}
                  >
                    {index === selectedIndex && (
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-sky-400 opacity-75"></span>
                    )}
                  </div>

                  <div className="space-y-3 pl-6">
                    <h3
                      className="h4"
                      data-tina-field={tinaFieldSafe(milestone, "title")}
                    >
                      {milestone.title}
                    </h3>
                    <div
                      className="text-primary-200 font-mono text-[15px]"
                      data-tina-field={tinaFieldSafe(milestone, "subtitle")}
                    >
                      {milestone.subtitle}
                    </div>
                  </div>

                  <div
                    className="space-y-3 flex flex-col items-start"
                    data-tina-field={tinaFieldSafe(milestone, "events")}
                  >
                    {milestone.events.map(
                      (event: string, eventIndex: number) => (
                        <div
                          key={eventIndex}
                          className="text-base border-b border-border pb-3  pl-6"
                        >
                          {event}
                        </div>
                      )
                    )}
                  </div>

                  {milestone.button?.label && milestone.button?.link && (
                    <div
                      className="pl-6 pb-4"
                      data-tina-field={tinaFieldSafe(milestone, "button")}
                    >
                      {milestone?.button?.label && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-auto min-h-10 whitespace-normal py-2.5 text-left text-balance"
                          href={milestone?.button?.link}
                        >
                          {milestone?.button?.label}
                          <span className="sr-only"> — {milestone.title}</span>
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      </div>
    </Section>
  );
}

function Investors({ investors }: { investors: any }) {
  const globalData = useGlobalData();
  const testimonials = globalData?.testimonials?.testimonials || [];

  return (
    <>
      <Section
        wrapperClassName={cn(
          "overflow-hidden",
          investors?.includeLogos && "!pb-64"
        )}
        className="space-y-6 md:space-y-12"
        backgroundVariant="pixels-subtle-reverse"
      >
        <div className="space-y-3">
          <h2
            className="h2"
            data-tina-field={tinaFieldSafe(investors, "title")}
          >
            {investors?.title}
          </h2>
          <p data-tina-field={tinaFieldSafe(investors, "subtitle")}>
            {investors?.subtitle}
          </p>
        </div>
        <TestimonialsCarousel testimonials={testimonials} />
      </Section>

      {investors?.includeLogos && <LogosCarousel />}
    </>
  );
}

function Awards({ awards }: { awards: any }) {
  return (
    <Section wrapperClassName="lg:!pb-32" className="space-y-8 lg:space-y-16">
      <div className="space-y-3">
        <h2 className="h2" data-tina-field={tinaFieldSafe(awards, "title")}>
          {awards?.title}
        </h2>
        <p data-tina-field={tinaFieldSafe(awards, "subtitle")}>
          {awards?.subtitle}
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {awards?.awards?.map((award: any, index: number) => (
          <Link href={award.link || "#"} key={index} target="_blank">
            <Card
              backgroundVariant="light"
              animateOnHover={true}
              wrapperClassName="min-h-[160px] md:min-h-[240px]"
              className="flex-1 flex flex-col justify-between group"
            >
              <CardHeader className="flex-1">
                <div className="flex justify-between items-center gap-4">
                  <TrophyIcon className="size-8 lg:size-10 text-primary-200 stroke-[1px]" />
                  {award?.logo && (
                    <div className="h-8 w-20 lg:h-10 lg:w-24">
                      <Image
                        src={award?.logo}
                        alt={award?.label}
                        width={64}
                        height={64}
                        quality={100}
                        data-tina-field={tinaFieldSafe(award, "logo")}
                        className="object-contain aspect-video h-full w-full opacity-100 group-hover:opacity-100 transition-opacity duration-300"
                      />
                    </div>
                  )}
                </div>
                <CardTitle
                  size="sm"
                  className="mt-auto"
                  data-tina-field={tinaFieldSafe(award, "label")}
                >
                  {award.label}
                </CardTitle>
              </CardHeader>
            </Card>
          </Link>
        ))}
      </div>
    </Section>
  );
}
